<script setup>
import { ref, computed } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useEmitter } from 'dashboard/composables/emitter';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem.vue';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu.vue';
import wootConstants from 'dashboard/constants/globals';
import {
  CMD_REOPEN_CONVERSATION,
  CMD_RESOLVE_CONVERSATION,
} from 'dashboard/helper/commandbar/events';

import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import BrainIcon from 'dashboard/components-next/icon/BrainIcon.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

const store = useStore();
const { t } = useI18n();
const { checkMissingAttributes } = useConversationRequiredAttributes();

const arrowDownButtonRef = ref(null);
const isLoading = ref(false);
const isBotToggling = ref(false);
const resolveAttributesModalRef = ref(null);

const [showActionsDropdown, toggleDropdown] = useToggle();
const closeDropdown = () => toggleDropdown(false);
const openDropdown = () => toggleDropdown(true);

const currentChat = computed(
  () => store.getters['unifiedView/getEffectiveChat']
);

const inbox = computed(() => {
  const inboxId = currentChat.value.inbox_id;
  return inboxId ? store.getters['inboxes/getInbox'](inboxId) : {};
});

const hasBotConfigured = computed(() => !!inbox.value.active_bot);
const isBotActive = computed(() => !!currentChat.value.bot_active);

// Captain assistant info (Camada 1)
const captainAssistant = computed(() => inbox.value.captain_assistant || null);
const assistantStatus = computed(() => captainAssistant.value?.status || null);
const assistantName = computed(() => captainAssistant.value?.name || '');

// Camada 1 — assistant pode responder nesta conversa?
const assistantCanRespondHere = computed(() => {
  if (!captainAssistant.value) return true; // sem captain, mantém comportamento de outros bots
  if (assistantStatus.value === 'active') return true;
  if (assistantStatus.value === 'testing') {
    const testIds = captainAssistant.value.test_conversation_ids || [];
    return testIds.includes(currentChat.value.id);
  }
  return false; // draft, paused
});

// Estado consolidado pra renderização do botão
const botUiState = computed(() => {
  if (!hasBotConfigured.value) return 'no_bot';

  if (assistantStatus.value === 'draft') return 'assistant_draft';
  if (assistantStatus.value === 'paused') return 'assistant_paused_global';

  if (assistantStatus.value === 'testing') {
    if (!assistantCanRespondHere.value) return 'testing_excluded';
    return isBotActive.value ? 'testing_active' : 'testing_paused_here';
  }

  // status === 'active' ou null (bot tradicional)
  return isBotActive.value ? 'active_on' : 'active_paused_here';
});

// Direção A — switch inline + rótulo. Cada estado define rótulo, tooltip, cores
// (Tailwind n-*), se mostra o switch e se está bloqueado (não clicável).
const BOT_STATE_STYLES = {
  active_on: {
    label: 'LABEL_BOT',
    tooltip: 'TOOLTIP_ON',
    pill: 'bg-n-teal-3 border-n-teal-6 text-n-teal-11',
    showSwitch: true,
    on: true,
    switchColor: 'bg-n-teal-9',
  },
  active_paused_here: {
    label: 'LABEL_BOT',
    tooltip: 'TOOLTIP_PAUSED',
    pill: 'bg-n-amber-3 border-n-amber-6 text-n-amber-11',
    showSwitch: true,
    on: false,
    switchColor: 'bg-n-amber-9',
  },
  testing_active: {
    label: 'LABEL_TEST',
    tooltip: 'TOOLTIP_TEST_ON',
    pill: 'bg-n-blue-3 border-n-blue-6 text-n-blue-11',
    showSwitch: true,
    on: true,
    switchColor: 'bg-n-blue-9',
  },
  testing_paused_here: {
    label: 'LABEL_TEST',
    tooltip: 'TOOLTIP_TEST_PAUSED',
    pill: 'bg-n-amber-3 border-n-amber-6 text-n-amber-11',
    showSwitch: true,
    on: false,
    switchColor: 'bg-n-amber-9',
  },
  assistant_draft: {
    label: 'LABEL_DRAFT',
    tooltip: 'TOOLTIP_DRAFT',
    pill: 'bg-n-slate-3 border-n-slate-5 text-n-slate-10',
    showSwitch: false,
    locked: true,
  },
  assistant_paused_global: {
    label: 'LABEL_PAUSED',
    tooltip: 'TOOLTIP_PAUSED_GLOBAL',
    pill: 'bg-n-slate-3 border-n-slate-5 text-n-slate-10',
    showSwitch: false,
    locked: true,
  },
  testing_excluded: {
    label: 'LABEL_BOT',
    tooltip: 'TOOLTIP_TEST_EXCLUDED',
    pill: 'bg-n-slate-3 border-n-slate-5 text-n-slate-10',
    showSwitch: false,
    locked: true,
  },
};

const botStyle = computed(() => BOT_STATE_STYLES[botUiState.value] || null);

const botTooltip = computed(() => {
  if (!botStyle.value) return t('CONVERSATION.BOT_TOGGLE.NO_BOT');
  return t(`CONVERSATION.BOT_TOGGLE.${botStyle.value.tooltip}`, {
    name: assistantName.value,
  });
});

const botLabel = computed(() =>
  t(
    `CONVERSATION.BOT_TOGGLE.${botStyle.value ? botStyle.value.label : 'LABEL_BOT'}`
  )
);

const botPillClass = computed(() => botStyle.value?.pill ?? '');
const botShowSwitch = computed(() => Boolean(botStyle.value?.showSwitch));
const botSwitchOn = computed(() => Boolean(botStyle.value?.on));
const botSwitchColor = computed(
  () => botStyle.value?.switchColor ?? 'bg-n-slate-6'
);
const botSwitchText = computed(() =>
  botSwitchOn.value
    ? t('CONVERSATION.BOT_TOGGLE.ON')
    : t('CONVERSATION.BOT_TOGGLE.OFF')
);
const botButtonDisabled = computed(() => Boolean(botStyle.value?.locked));

const isOpen = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.OPEN
);
const isPending = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.PENDING
);
const isResolved = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.RESOLVED
);
const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const showAdditionalActions = computed(
  () => !isPending.value && !isSnoozed.value
);

const showOpenButton = computed(() => {
  return isPending.value || isSnoozed.value;
});

const toggleBotStatus = async () => {
  isBotToggling.value = true;
  try {
    await store.dispatch('toggleBot', {
      conversationId: currentChat.value.id,
    });
    const label = currentChat.value.bot_active
      ? t('CONVERSATION.BOT_TOGGLE.ACTIVATED')
      : t('CONVERSATION.BOT_TOGGLE.DEACTIVATED');
    useAlert(label);
  } catch {
    useAlert(t('CONVERSATION.BOT_TOGGLE.FAILED'));
  } finally {
    isBotToggling.value = false;
  }
};

const getConversationParams = () => {
  const allConversations = document.querySelectorAll(
    '.conversations-list .conversation'
  );

  const activeConversation = document.querySelector(
    'div.conversations-list div.conversation.active'
  );
  const activeConversationIndex = [...allConversations].indexOf(
    activeConversation
  );
  const lastConversationIndex = allConversations.length - 1;

  return {
    all: allConversations,
    activeIndex: activeConversationIndex,
    lastIndex: lastConversationIndex,
  };
};

const openSnoozeModal = () => {
  const ninja = document.querySelector('ninja-keys');
  ninja.open({ parent: 'snooze_conversation' });
};

const toggleStatus = async (status, snoozedUntil, customAttributes = null) => {
  closeDropdown();
  isLoading.value = true;

  const payload = {
    conversationId: currentChat.value.id,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  try {
    await store.dispatch('toggleStatus', payload);
    useAlert(t('CONVERSATION.CHANGE_STATUS'));
  } catch {
    useAlert(t('CONVERSATION.CHANGE_STATUS_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const handleResolveWithAttributes = ({ attributes, context }) => {
  if (context) {
    const currentCustomAttributes = currentChat.value.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };
    toggleStatus(
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
};

const onCmdOpenConversation = () => {
  toggleStatus(wootConstants.STATUS_TYPE.OPEN);
};

const onCmdResolveConversation = () => {
  const currentCustomAttributes = currentChat.value.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    const conversationContext = {
      id: currentChat.value.id,
      snoozedUntil: null,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleStatus(wootConstants.STATUS_TYPE.RESOLVED);
  }
};

const keyboardEvents = {
  'Alt+KeyM': {
    action: () => arrowDownButtonRef.value?.$el.click(),
    allowOnFocusedInput: true,
  },
  'Alt+KeyE': {
    action: async () => {
      onCmdResolveConversation();
    },
  },
  '$mod+Alt+KeyE': {
    action: async event => {
      const { all, activeIndex, lastIndex } = getConversationParams();
      onCmdResolveConversation();

      if (activeIndex < lastIndex) {
        all[activeIndex + 1].click();
      } else if (all.length > 1) {
        all[0].click();
        document.querySelector('.conversations-list').scrollTop = 0;
      }
      event.preventDefault();
    },
  },
};

useKeyboardEvents(keyboardEvents);

useEmitter(CMD_REOPEN_CONVERSATION, onCmdOpenConversation);
useEmitter(CMD_RESOLVE_CONVERSATION, onCmdResolveConversation);
</script>

<template>
  <div class="flex relative justify-end items-center gap-1.5 resolve-actions">
    <button
      v-if="hasBotConfigured"
      v-tooltip="botTooltip"
      type="button"
      class="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold transition-colors flex-shrink-0 disabled:cursor-not-allowed"
      :class="botPillClass"
      :disabled="botButtonDisabled || isBotToggling"
      @click="toggleBotStatus"
    >
      <BrainIcon :size="16" class="flex-shrink-0" />
      <span>{{ botLabel }}</span>
      <span
        v-if="botShowSwitch"
        class="relative h-4 rounded-full transition-colors w-7 flex-shrink-0"
        :class="botSwitchOn ? botSwitchColor : 'bg-n-slate-6'"
      >
        <span
          class="absolute w-3 h-3 transition-transform bg-white rounded-full shadow-sm top-0.5 ltr:left-0.5 rtl:right-0.5"
          :class="
            botSwitchOn
              ? 'ltr:translate-x-3 rtl:-translate-x-3'
              : 'translate-x-0'
          "
        />
      </span>
      <span v-if="botShowSwitch" class="text-[10px] font-medium opacity-70">
        {{ botSwitchText }}
      </span>
    </button>
    <ButtonGroup
      class="flex-shrink-0 rounded-lg shadow-sm outline-1 outline outline-transparent"
    >
      <Button
        v-if="isOpen"
        :label="t('CONVERSATION.HEADER.RESOLVE_ACTION')"
        icon="i-lucide-circle-check-big"
        size="xs"
        color="slate"
        variant="faded"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdResolveConversation"
      />
      <Button
        v-else-if="isResolved"
        :label="t('CONVERSATION.HEADER.REOPEN_ACTION')"
        icon="i-lucide-inbox"
        size="xs"
        color="amber"
        variant="faded"
        no-animation
        class="ltr:rounded-r-none rtl:rounded-l-none !outline-0"
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-else-if="showOpenButton"
        :label="t('CONVERSATION.HEADER.OPEN_ACTION')"
        icon="i-lucide-inbox"
        size="xs"
        color="blue"
        variant="faded"
        no-animation
        :is-loading="isLoading"
        @click="onCmdOpenConversation"
      />
      <Button
        v-if="showAdditionalActions"
        ref="arrowDownButtonRef"
        icon="i-lucide-chevron-down"
        :disabled="isLoading"
        size="xs"
        no-animation
        class="ltr:rounded-l-none rtl:rounded-r-none !outline-0"
        :color="isOpen ? 'slate' : isResolved ? 'amber' : 'blue'"
        :variant="isOpen ? 'faded' : 'faded'"
        trailing-icon
        @click="openDropdown"
      />
    </ButtonGroup>
    <div
      v-if="showActionsDropdown"
      v-on-clickaway="closeDropdown"
      class="border rounded-lg shadow-lg border-n-strong dark:border-n-strong box-content p-2 w-fit z-10 bg-n-alpha-3 backdrop-blur-[100px] absolute block left-auto top-full mt-0.5 start-0 xl:start-auto xl:end-0 max-w-[12.5rem] min-w-[9.75rem] [&_ul>li]:mb-0"
    >
      <WootDropdownMenu class="mb-0">
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.SNOOZE_UNTIL')"
            ghost
            slate
            sm
            start
            icon="i-lucide-alarm-clock-minus"
            class="w-full"
            @click="() => openSnoozeModal()"
          />
        </WootDropdownItem>
        <WootDropdownItem v-if="!isPending">
          <Button
            :label="t('CONVERSATION.RESOLVE_DROPDOWN.MARK_PENDING')"
            ghost
            slate
            sm
            start
            icon="i-lucide-circle-dot-dashed"
            class="w-full"
            @click="() => toggleStatus(wootConstants.STATUS_TYPE.PENDING)"
          />
        </WootDropdownItem>
      </WootDropdownMenu>
    </div>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>
